import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Clock, Plus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEventTiming } from './EventTimingContext';
import { SortableTimingRow } from './SortableTimingRow';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { COMPULSORY_DANCE_TIME_OPTIONS } from './constants';

export default function CompulsoryDanceTab() {
  const {
    isSuperUser,
    schedules,
    dialogOpen,
    setDialogOpen,
    editingTiming,
    setEditingTiming,
    saving,
    setSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    deleting,
    handleDeleteClick,
    handleConfirmDelete,
    selectedScheduleId,
    setSelectedScheduleId,
    minutesPostWarmup,
    setMinutesPostWarmup,
    addForPanelSwap,
    setAddForPanelSwap,
    useSoloForCouples,
    setUseSoloForCouples,
    maxSkatersWarmupCadetSenior,
    setMaxSkatersWarmupCadetSenior,
    maxSkatersWarmupOther,
    setMaxSkatersWarmupOther,
    timingsLists,
    fetchTimings,
    sensors,
    handleDragEnd,
    canEditTiming,
    resetForm,
    formatTimeOnBlur,
    shouldShowHR,
  } = useEventTiming();

  const [compulsoryDanceGrades, setCompulsoryDanceGrades] = useState([]);
  const [compulsoryDanceTimings, setCompulsoryDanceTimings] = useState({});

  const timingsList = timingsLists['compulsory-dance'] || [];

  // Fetch Compulsory Dance grades when schedule is selected
  useEffect(() => {
    if (!selectedScheduleId || editingTiming) return;

    const fetchCompulsoryDanceGrades = async () => {
      try {
        const res = await axios.get(`${API}/event-timings/compulsory-dance-grades/${selectedScheduleId}`, { withCredentials: true });
        const grades = res.data || [];
        
        setCompulsoryDanceGrades(grades);
        
        const timings = {};
        grades.forEach(g => {
          timings[g.grade_id] = {
            warmup_per_dance: '3:00',
            timing_per_dance: '3:00',
            can_shorten: false
          };
        });
        setCompulsoryDanceTimings(timings);
      } catch (error) {
        console.error('Error fetching compulsory dance grades:', error);
        toast.error('Failed to load grades for this schedule');
      }
    };
    fetchCompulsoryDanceGrades();
  }, [selectedScheduleId, editingTiming]);

  const localResetForm = () => {
    resetForm();
    setCompulsoryDanceGrades([]);
    setCompulsoryDanceTimings({});
  };

  const handleAddTimings = () => {
    localResetForm();
    setDialogOpen(true);
  };

  const handleEditTiming = async (timing) => {
    setEditingTiming(timing);
    setSelectedScheduleId(timing.schedule_id);
    setMinutesPostWarmup(timing.minutes_post_warmup_first_skater || '1:00');
    setAddForPanelSwap(timing.add_for_panel_swap || '1:00');
    setUseSoloForCouples(timing.use_solo_for_couples ?? true);
    setMaxSkatersWarmupCadetSenior(timing.max_skaters_warmup_cadet_senior ?? 5);
    setMaxSkatersWarmupOther(timing.max_skaters_warmup_other ?? 6);
    
    try {
      const res = await axios.get(`${API}/event-timings/compulsory-dance-grades/${timing.schedule_id}`, { withCredentials: true });
      const grades = res.data || [];
      
      setCompulsoryDanceGrades(grades);
      
      const savedTimingsMap = {};
      (timing.grade_timings || []).forEach(gt => {
        savedTimingsMap[gt.grade_id] = gt;
      });
      
      const timings = {};
      grades.forEach(g => {
        const saved = savedTimingsMap[g.grade_id];
        timings[g.grade_id] = {
          warmup_per_dance: saved?.warmup_per_dance || '3:00',
          timing_per_dance: saved?.timing_per_dance || '3:00',
          can_shorten: saved?.can_shorten || false
        };
      });
      setCompulsoryDanceTimings(timings);
    } catch (error) {
      console.error('Error fetching compulsory dance grades for edit:', error);
      toast.error('Failed to load timing data');
      return;
    }
    
    setDialogOpen(true);
  };

  const updateTiming = (gradeId, field, value) => {
    setCompulsoryDanceTimings(prev => ({
      ...prev,
      [gradeId]: { ...prev[gradeId], [field]: value }
    }));
  };

  const handleSave = async () => {
    if (!selectedScheduleId) {
      toast.error('Please select a schedule');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        schedule_id: selectedScheduleId,
        event_type: 'compulsory-dance',
        grade_timings: Object.entries(compulsoryDanceTimings).map(([gradeId, timing]) => ({
          grade_id: gradeId,
          ...timing
        })),
        minutes_post_warmup_first_skater: minutesPostWarmup,
        add_for_panel_swap: addForPanelSwap,
        use_solo_for_couples: useSoloForCouples,
        max_skaters_warmup_cadet_senior: maxSkatersWarmupCadetSenior,
        max_skaters_warmup_other: maxSkatersWarmupOther
      };

      await axios.post(`${API}/event-timings`, payload, { withCredentials: true });
      
      toast.success(editingTiming ? 'Compulsory Dance timings updated successfully' : 'Compulsory Dance timings saved successfully');
      setDialogOpen(false);
      localResetForm();
      fetchTimings('compulsory-dance');
    } catch (error) {
      console.error('Error saving timings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  const renderGradesTable = () => (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-green-100">
          <th className="border px-3 py-2 text-left w-48">Grade</th>
          <th className="border px-3 py-2 text-center w-32">Warm up per Dance</th>
          <th className="border px-3 py-2 text-center w-32">Timing per Dance</th>
          <th className="border px-3 py-2 text-center w-24">Can Shorten</th>
        </tr>
      </thead>
      <tbody>
        {compulsoryDanceGrades.map((grade, index) => {
          const timing = compulsoryDanceTimings[grade.grade_id] || {};
          const showHR = shouldShowHR(compulsoryDanceGrades, index);
          
          return (
            <React.Fragment key={grade.grade_id}>
              {showHR && (
                <tr>
                  <td colSpan={4} style={{ border: 'none', padding: '16px 0', background: 'white' }}>
                    <div style={{ height: '2px', backgroundColor: '#a8a29e' }}></div>
                  </td>
                </tr>
              )}
              <tr>
                <td className="border px-3 py-2 bg-white font-medium">{grade.name}</td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.warmup_per_dance || '3:00'} 
                    onValueChange={(v) => updateTiming(grade.grade_id, 'warmup_per_dance', v)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {COMPULSORY_DANCE_TIME_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-1 py-1 bg-white">
                  <Select 
                    value={timing.timing_per_dance || '3:00'} 
                    onValueChange={(v) => updateTiming(grade.grade_id, 'timing_per_dance', v)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                      {COMPULSORY_DANCE_TIME_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="border px-3 py-2 bg-white text-center">
                  <Checkbox
                    checked={timing.can_shorten || false}
                    onCheckedChange={(checked) => updateTiming(grade.grade_id, 'can_shorten', checked)}
                  />
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Compulsory Dance</h2>
        <Button onClick={handleAddTimings} className="btn-primary" data-testid="add-timings-compulsory-dance">
          <Plus className="mr-2 h-4 w-4" />
          Add Timings
        </Button>
      </div>
      
      {!isSuperUser && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          You can create and edit timing tables for schedules you own. Timing tables for system schedules are view-only.
        </div>
      )}
      
      <Card className="p-6">
        {timingsList.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 text-stone-300" />
            <p>No timing entries yet for Compulsory Dance.</p>
            <p className="text-sm mt-1">Click "Add Timings" to create your first entry.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, 'compulsory-dance')}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="w-8"></th>
                    <th className="text-left py-3 px-2 font-semibold">Schedule</th>
                    <th className="text-center py-3 px-2 font-semibold">Grades Configured</th>
                    <th className="text-center py-3 px-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={timingsList.map(t => t.timing_id)} strategy={verticalListSortingStrategy}>
                    {timingsList.map(timing => (
                      <SortableTimingRow
                        key={timing.timing_id}
                        id={timing.timing_id}
                        timing={timing}
                        canEdit={canEditTiming(timing)}
                        canReorder={isSuperUser}
                        onEdit={handleEditTiming}
                        onDelete={handleDeleteClick}
                      />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </div>
          </DndContext>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(val) => { setDialogOpen(val); if (!val) localResetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTiming ? 'Edit Compulsory Dance Timings' : 'Add Compulsory Dance Timings'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label>Select Schedule *</Label>
              <Select 
                value={selectedScheduleId} 
                onValueChange={setSelectedScheduleId}
                disabled={!!editingTiming}
              >
                <SelectTrigger data-testid="schedule-select">
                  <SelectValue placeholder="Choose a schedule..." />
                </SelectTrigger>
                <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                  {schedules.map(s => (
                    <SelectItem key={s.schedule_id} value={s.schedule_id}>
                      {s.schedule_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingTiming && (
                <p className="text-xs text-muted-foreground mt-1">Schedule cannot be changed when editing</p>
              )}
            </div>

            {compulsoryDanceGrades.length > 0 && (
              <div className="overflow-x-auto">
                {renderGradesTable()}
              </div>
            )}

            {compulsoryDanceGrades.length > 0 && (
              <div className="space-y-4 pt-4 border-t">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Minutes post warm up - first skater</Label>
                    <Input
                      value={minutesPostWarmup}
                      onChange={(e) => setMinutesPostWarmup(e.target.value)}
                      onBlur={() => formatTimeOnBlur(minutesPostWarmup, setMinutesPostWarmup)}
                      placeholder="1:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:30)</p>
                  </div>
                  
                  <div>
                    <Label>Add for panel swap - men in group</Label>
                    <Input
                      value={addForPanelSwap}
                      onChange={(e) => setAddForPanelSwap(e.target.value)}
                      onBlur={() => formatTimeOnBlur(addForPanelSwap, setAddForPanelSwap)}
                      placeholder="1:00"
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Format: m:ss (e.g., 1:00)</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Max skaters per warm up group</Label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-sm text-muted-foreground">Cadet-Senior & Couples</Label>
                        <Select 
                          value={String(maxSkatersWarmupCadetSenior)} 
                          onValueChange={(v) => setMaxSkatersWarmupCadetSenior(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Other Skaters</Label>
                        <Select 
                          value={String(maxSkatersWarmupOther)} 
                          onValueChange={(v) => setMaxSkatersWarmupOther(parseInt(v))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]" position="popper" sideOffset={5}>
                            {[5, 6, 7, 8, 9, 10].map(n => (
                              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Use solo timings for same grade couples event?</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="useSoloForCouplesCD"
                          checked={useSoloForCouples === true}
                          onChange={() => setUseSoloForCouples(true)}
                        />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="useSoloForCouplesCD"
                          checked={useSoloForCouples === false}
                          onChange={() => setUseSoloForCouples(false)}
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={!selectedScheduleId || compulsoryDanceGrades.length === 0 || saving}
                className="btn-primary"
              >
                {saving ? 'Saving...' : (editingTiming ? 'Update Timings' : 'Save Timings')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemName={itemToDelete?.schedule_name ? `timing for "${itemToDelete.schedule_name}"` : 'this timing'}
        loading={deleting}
      />
    </>
  );
}
